/**
 * GuardianConfirmation - Erziehungsberechtigten-Bestätigung für Minderjährige
 *
 * Wird angezeigt wenn ein Bestandspatient unter 18 Jahre alt ist.
 * Der Erziehungsberechtigte muss bestätigen, dass er die Anamnese ausfüllt.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Alert
} from '@mui/material';
import {
  ChildCare as ChildIcon
} from '@mui/icons-material';

const GuardianConfirmation = ({ patientName, patientAge, onConfirm, onCancel }) => {
  const [guardianName, setGuardianName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [otherRelationship, setOtherRelationship] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  const validateAndSubmit = () => {
    const newErrors = {};

    if (!guardianName.trim()) {
      newErrors.guardianName = 'Bitte geben Sie Ihren Namen ein';
    }

    if (!relationship) {
      newErrors.relationship = 'Bitte wählen Sie Ihre Beziehung zum Kind';
    }

    if (relationship === 'sonstiges' && !otherRelationship.trim()) {
      newErrors.otherRelationship = 'Bitte spezifizieren Sie Ihre Beziehung';
    }

    if (!confirmed) {
      newErrors.confirmed = 'Bitte bestätigen Sie, dass Sie erziehungsberechtigt sind';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Beziehungs-Label für Anzeige (großgeschrieben)
    const getLabel = (rel) => {
      const labels = { mutter: 'Mutter', vater: 'Vater', vormund: 'Vormund' };
      return labels[rel] || rel;
    };

    onConfirm({
      guardianName: guardianName.trim(),
      relationship: relationship === 'sonstiges' ? otherRelationship.trim() : getLabel(relationship),
      confirmed: true,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: { xs: '100%', sm: 500 },
          width: '100%',
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <ChildIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" color="primary" fontWeight={600}>
            Anamnese für Minderjährige
          </Typography>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Patient:</strong> {patientName} ({patientAge} Jahre)
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Da der Patient minderjährig ist, muss die Anamnese von einem
            Erziehungsberechtigten ausgefüllt und unterschrieben werden.
          </Typography>
        </Alert>

        {/* Form */}
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Guardian Name */}
          <TextField
            label="Name des Erziehungsberechtigten"
            value={guardianName}
            onChange={(e) => {
              setGuardianName(e.target.value);
              if (errors.guardianName) {
                setErrors({ ...errors, guardianName: null });
              }
            }}
            placeholder="Vor- und Nachname"
            required
            fullWidth
            error={!!errors.guardianName}
            helperText={errors.guardianName}
          />

          {/* Relationship */}
          <FormControl error={!!errors.relationship}>
            <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
              Beziehung zum Kind *
            </FormLabel>
            <RadioGroup
              value={relationship}
              onChange={(e) => {
                setRelationship(e.target.value);
                if (errors.relationship) {
                  setErrors({ ...errors, relationship: null });
                }
              }}
            >
              <FormControlLabel
                value="mutter"
                control={<Radio />}
                label="Mutter"
              />
              <FormControlLabel
                value="vater"
                control={<Radio />}
                label="Vater"
              />
              <FormControlLabel
                value="vormund"
                control={<Radio />}
                label="Vormund"
              />
              <FormControlLabel
                value="sonstiges"
                control={<Radio />}
                label="Sonstiges"
              />
            </RadioGroup>
            {errors.relationship && (
              <Typography variant="caption" color="error">
                {errors.relationship}
              </Typography>
            )}
          </FormControl>

          {/* Other Relationship */}
          {relationship === 'sonstiges' && (
            <TextField
              label="Bitte spezifizieren"
              value={otherRelationship}
              onChange={(e) => {
                setOtherRelationship(e.target.value);
                if (errors.otherRelationship) {
                  setErrors({ ...errors, otherRelationship: null });
                }
              }}
              fullWidth
              error={!!errors.otherRelationship}
              helperText={errors.otherRelationship}
            />
          )}

          {/* Confirmation Checkbox */}
          <FormControl error={!!errors.confirmed}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => {
                    setConfirmed(e.target.checked);
                    if (errors.confirmed) {
                      setErrors({ ...errors, confirmed: null });
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Ich bestätige, dass ich erziehungsberechtigt für <strong>{patientName}</strong> bin
                  und die Anamnese stellvertretend ausfüllen darf.
                </Typography>
              }
            />
            {errors.confirmed && (
              <Typography variant="caption" color="error" sx={{ ml: 4 }}>
                {errors.confirmed}
              </Typography>
            )}
          </FormControl>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            fullWidth
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={validateAndSubmit}
            fullWidth
            disabled={!guardianName || !relationship || !confirmed}
          >
            Weiter zur Anamnese
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default GuardianConfirmation;
